import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { environment } from '@env/environment';
import { SecureImage } from '@shared/components/secured-resource/bg-image/bg-image.models';
import { SecuredResourceResolverService } from '@shared/components/secured-resource/secured-resource-resolver.service';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

/**
 * should pass the image url of the s3 bucket and the component returns the blob url
 * Can also over ride styles in your component
 * @example
 * ```
 * .selected-image-border {
  height: 100px;
  ::ng-deep .image-container {
    width: 100%;
    height: 100%;
  }
}
```
 */
@Component({
  // tslint:disable-next-line
  selector: '[miq-bg-image]',
  template: `
    <div class="image-icons">
      <mat-icon
        *ngIf="showImage && !publishable && !isPDF"
        class="no-publish-icon"
        [matTooltip]="'recordsSearch.notForPublish' | xTranslate"
        [ngClass]="bgClass"
        svgIcon="icon-no-publish"
      ></mat-icon>
      <ng-content></ng-content>
    </div>
    <ng-container *ngIf="showImage">
      <ng-container *ngIf="backgroundImage$ | async as backgroundImage">
        <div
          *ngIf="!disableBGCoverImage; else overFlowImage"
          [ngClass]="color"
          class="image-container"
          [ngStyle]="backgroundImage"
        ></div>

        <ng-template #overFlowImage>
          <img
            alt="Full image"
            class="image"
            [ngClass]="[color]"
            [class.cover-image]="!fullImage"
            [src]="sanitizedImgSrc$ | async"
          />
        </ng-template>
      </ng-container>
    </ng-container>
  `,
  styleUrls: ['./bg-image.component.scss'],
  standalone: false,
})
export class BgImageComponent implements OnChanges, OnInit, OnDestroy {
  @Input() imageSrc: string;
  @Input() publishable: boolean;
  @Input() color = 'default';
  @Input() showImage: boolean = true;
  @Input() bgClass = ''; // TODO: rename to something more meaningful
  /**
   * Disbale the div strategy to use an image to keep the real size
   */
  @Input() disableBGCoverImage: boolean = false;
  /**
   * When disableBGCoverImage = true this flag works to define if we resize or not the image
   */
  @Input() fullImage: boolean = true;

  @Input() set secureImage(imageObj: SecureImage) {
    if (imageObj != null) {
      this.imageSrc = typeof imageObj.imageSrc === 'string' ? (imageObj.imageSrc as string) : null;
      this.publishable = imageObj.publishable;
      this.isPDF = imageObj.isPDF;
    }
  }

  public isPDF: boolean;
  public imageSrc$ = new BehaviorSubject<string>(null);
  public sanitizedImgSrc$: Observable<Object> | null = null;
  public backgroundImage$ = this.imageSrc$.pipe(
    filter(url => !!url),
    switchMap(url => this.loadImage(url).pipe(catchError(() => of(environment.appConstants.BUILDING_PH_IMAGE)))),
    tap(url => (this.sanitizedImgSrc$ = this.securedResourceResolver.loadSanitizedImage(url))),
    map(url => this.getBackgroundImageStyle(url))
  );

  constructor(private securedResourceResolver: SecuredResourceResolverService) {}

  ngOnChanges() {
    this.imageSrc$.next(this.imageSrc);
  }

  ngOnInit() {
    /* required by Angular-l10n */
  }

  ngOnDestroy() {
    /* required by Angular-l10n */
  }

  loadImage(imageSrc) {
    if (imageSrc === environment.appConstants.BUILDING_PH_IMAGE) {
      return of(imageSrc);
    }
    return this.securedResourceResolver.loadImage(imageSrc);
  }

  public getBackgroundImageStyle(imageSrc) {
    if (imageSrc === environment.appConstants.BUILDING_PH_IMAGE) {
      // if load image catches an error, then image path will be default image and
      //  it has to contain color otherwise the default image will not be seen.
      this.color = this.color === 'image-container' ? 'image-container default' : this.color;
      // for svg coloringq
      return {
        mask: `url(${imageSrc})`,
        '-webkit-mask': `url(${imageSrc})`,
        '-webkit-mask-size': 'cover',
      };
    }
    return {
      'background-image': `url(${imageSrc})`,
      'background-position': 'center center',
      'background-size': 'cover',
      'background-repeat': 'no-repeat',
    };
  }
}
